import React, { useState, useEffect, useMemo } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import DataTable from "react-data-table-component";
import Select from "react-select";

function AttendanceReport() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, selectedYear]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedMonth) params.append("month", selectedMonth);
      if (selectedYear) params.append("year", selectedYear);
      const response = await axiosInstance.get(`/api/attendance?${params.toString()}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error("Failed to fetch attendance:", error);
    }
    setLoading(false);
  };

  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const processGridData = (data) => {
    const grouped = {};
    data.forEach(record => {
      if (!grouped[record.EmployeeID]) {
        grouped[record.EmployeeID] = {
          EmployeeID: record.EmployeeID,
          WorkerName: record.WorkerName || "Unknown",
          Department: record.Department || "Unknown",
          attendanceMap: {},
          timeMap: {},
          rawTimeMap: {},
        };
      }
      if (record.LoginTime) {
        const loginDate = new Date(record.LoginTime);
        const day = loginDate.getDate();
        grouped[record.EmployeeID].attendanceMap[day] = "P";
        
        const formatTime = (dateStr) => {
          if (!dateStr) return "--:--";
          const d = new Date(dateStr);
          return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        };
        const inT = formatTime(record.LoginTime);
        const outT = record.LogoutTime ? formatTime(record.LogoutTime) : "Active";
        grouped[record.EmployeeID].timeMap[day] = `In: ${inT} | Out: ${outT}`;
        grouped[record.EmployeeID].rawTimeMap[day] = { in: inT, out: outT };
      }
    });

    return Object.values(grouped).map(emp => {
      let presentCount = 0;
      let absentCount = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      daysArray.forEach(day => {
        const dateObj = new Date(selectedYear, selectedMonth - 1, day);
        const isFuture = dateObj > today;

        if (emp.attendanceMap[day] === "P") {
          presentCount++;
        } else if (isFuture) {
          emp.attendanceMap[day] = "-";
        } else {
          emp.attendanceMap[day] = "A";
          absentCount++;
        }
      });

      emp.TotalPresent = presentCount;
      emp.TotalAbsent = absentCount;
      return emp;
    });
  };

  const fullGridData = useMemo(() => processGridData(attendanceData), [attendanceData, selectedMonth, selectedYear]);

  const gridData = useMemo(() => {
    if (!selectedWorker) return fullGridData;
    return fullGridData.filter(emp => emp.EmployeeID === selectedWorker.value);
  }, [fullGridData, selectedWorker]);

  const workerOptions = useMemo(() =>
    fullGridData.map(emp => ({
      value: emp.EmployeeID,
      label: `${emp.WorkerName} (${emp.EmployeeID})`
    })), [fullGridData]);

  const avgDaily = gridData.length > 0
    ? Math.round(gridData.reduce((acc, curr) => acc + curr.TotalPresent, 0) / daysInMonth)
    : 0;

  const totalPresent = gridData.reduce((acc, curr) => acc + curr.TotalPresent, 0);
  const totalAbsent = gridData.reduce((acc, curr) => acc + curr.TotalAbsent, 0);
  const attendanceRate = totalPresent + totalAbsent > 0
    ? Math.round((totalPresent / (totalPresent + totalAbsent)) * 100)
    : 0;

  const baseColumns = [
    {
      name: "EMP ID",
      selector: row => row.EmployeeID,
      width: '80px',
      cell: row => (
        <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 600 }}>
          #{row.EmployeeID}
        </span>
      )
    },
    {
      name: "Name",
      selector: row => row.WorkerName,
      width: '180px',
      sortable: true,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
            background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            color: '#2563eb', border: '1px solid #bfdbfe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: 'bold'
          }}>
            {row.WorkerName.charAt(0)}
          </div>
          <span style={{ color: '#1e293b', fontSize: '13px', fontWeight: 600 }}>
            {row.WorkerName}
          </span>
        </div>
      )
    },
    {
      name: "Dept",
      selector: row => row.Department,
      width: '130px',
      sortable: true,
      cell: row => (
        <span style={{
          background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
          padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600
        }}>
          {row.Department}
        </span>
      )
    },
  ];

  const dayColumns = daysArray.map(day => {
    return {
      name: (
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>{day}</div>
        </div>
      ),
      selector: row => row.attendanceMap[day],
      width: '68px',
      minWidth: '68px',
      center: true,
      cell: row => {
        const status = row.attendanceMap[day];
        const times = row.rawTimeMap?.[day];
        const styles = {
          P: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
          A: { bg: '#fee2e2', color: '#991b1b', border: '#fecaca' },
          '-': { bg: '#f8fafc', color: '#cbd5e1', border: '#f1f5f9' },
        };
        const s = styles[status] || styles['-'];
        return (
          <div style={{
            width: '100%', height: '100%',
            background: s.bg,
            color: s.color, fontSize: '11px', fontWeight: 700,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            margin: '0', padding: '2px 0'
          }}>
            {status === 'P' && times ? (
              <div style={{ fontSize: '9px', lineHeight: '1.2', textAlign: 'center' }}>
                <div style={{ color: '#15803d', fontWeight: 800 }} title="In Time">in {times.in}</div>
                <div style={{ color: '#166534', fontWeight: 600, borderTop: '1px solid rgba(22,101,52,0.2)', marginTop: '2px', paddingTop: '2px' }} title="Out Time">out {times.out}</div>
              </div>
            ) : (
               status === '-' ? '' : status
            )}
          </div>
        );
      }
    };
  });

  const totalColumns = [
    {
      name: "Present",
      selector: row => row.TotalPresent,
      width: '80px',
      center: true,
      sortable: true,
      cell: row => (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '4px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '12px' }}>
          {row.TotalPresent}
        </div>
      )
    },
    {
      name: "Absent",
      selector: row => row.TotalAbsent,
      width: '80px',
      center: true,
      sortable: true,
      cell: row => (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '12px' }}>
          {row.TotalAbsent}
        </div>
      )
    },
  ];

  const columns = [...baseColumns, ...dayColumns, ...totalColumns];

  const handleExportCSV = () => {
    const csvHeader = ["EmployeeID", "WorkerName", "Department", ...daysArray, "Total Present", "Total Absent"].join(",");
    const csvRows = gridData.map(row => {
      const dayValues = daysArray.map(d => {
        const status = row.attendanceMap[d];
        if (status === "P") return row.timeMap[d] || "Present";
        if (status === "A") return "Absent";
        return status;
      });
      return [row.EmployeeID, row.WorkerName, row.Department, ...dayValues, row.TotalPresent, row.TotalAbsent]
        .map(val => `"${val}"`).join(",");
    });
    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_${selectedMonth}_${selectedYear}.csv`;
    link.click();
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      height: '38px',
      background: '#fff',
      borderColor: state.isFocused ? '#2563eb' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(37,99,235,0.2)' : 'none',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      '&:hover': { borderColor: '#cbd5e1' }
    }),
    menu: (base) => ({ ...base, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 999, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected ? '#2563eb' : state.isFocused ? '#f1f5f9' : 'transparent',
      color: state.isSelected ? '#fff' : '#1e293b', 
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer'
    }),
    singleValue: (base) => ({ ...base, color: '#1e293b' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '13px' }),
    valueContainer: (base) => ({ ...base, padding: '0 12px' }),
    indicatorsContainer: (base) => ({ ...base, height: '38px' }),
    clearIndicator: (base) => ({ ...base, color: '#94a3b8', '&:hover': { color: '#ef4444' } }),
    dropdownIndicator: (base) => ({ ...base, color: '#94a3b8' }),
  };

  const tableCustomStyles = {
    table: { style: { background: '#fff' } },
    tableWrapper: { style: { background: '#fff', borderRadius: '0 0 12px 12px' } },
    headRow: {
      style: {
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        minHeight: '45px',
      }
    },
    headCells: {
      style: {
        color: '#475569',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '0 0px',
        justifyContent: 'center',
        borderRight: '1px solid #e2e8f0',
      }
    },
    rows: {
      style: {
        background: '#fff',
        borderBottom: '1px solid #f1f5f9',
        minHeight: '48px',
        '&:hover': { background: '#f8fafc' },
      }
    },
    cells: {
      style: {
        padding: '0',
        justifyContent: 'center',
        borderRight: '1px solid #f1f5f9',
        borderBottom: '1px solid #f1f5f9',
      }
    },
    pagination: {
      style: {
        background: '#fff',
        borderTop: '1px solid #e2e8f0',
        color: '#475569',
        fontSize: '13px',
        fontWeight: 500,
        borderRadius: '0 0 12px 12px'
      },
    },
    progress: { style: { background: '#fff', color: '#2563eb' } },
    noData: { style: { background: '#fff', color: '#64748b' } },
  };

  const monthName = new Date(0, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' });

  const kpiCards = [
    {
      label: 'Total Workforce',
      value: `${gridData.length}`,
      sub: 'Active employees this month',
      icon: 'bi bi-people-fill',
      accent: '#3b82f6',
      bg: '#eff6ff',
      border: '#bfdbfe'
    },
    {
      label: 'Avg Daily Attendance',
      value: `${avgDaily}`,
      sub: 'Workers present per day',
      icon: 'bi bi-graph-up-arrow',
      accent: '#10b981',
      bg: '#ecfdf5',
      border: '#a7f3d0'
    },
    {
      label: 'Attendance Rate',
      value: `${attendanceRate}%`,
      sub: 'Overall healthy metric',
      icon: 'bi bi-pie-chart-fill',
      accent: attendanceRate >= 85 ? '#10b981' : attendanceRate >= 70 ? '#f59e0b' : '#ef4444',
      bg: attendanceRate >= 85 ? '#ecfdf5' : attendanceRate >= 70 ? '#fffbeb' : '#fef2f2',
      border: attendanceRate >= 85 ? '#a7f3d0' : attendanceRate >= 70 ? '#fde68a' : '#fecaca'
    },
    {
      label: 'Total Absences',
      value: `${totalAbsent}`,
      sub: 'Total absent days marked',
      icon: 'bi bi-exclamation-triangle-fill',
      accent: '#ef4444',
      bg: '#fef2f2',
      border: '#fecaca'
    },
  ];

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
        <Header
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="main-container" style={{ padding: '24px 32px', background: '#f4f7fe', minHeight: 'calc(100vh - 60px)', minWidth: 0, overflowX: 'hidden' }}>
          
          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', margin: '0 0 4px 0' }}>
                <i className="bi bi-calendar2-check-fill text-primary me-2"></i> 
                Worker Attendance Timesheet
              </h2>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px', fontWeight: 500, paddingLeft: '32px' }}>
                Manage and track worker attendance for {monthName} {selectedYear}
              </p>
            </div>
            
            <button onClick={handleExportCSV} style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              color: '#1e293b',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#10b981'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#1e293b'; }}
            >
              <i className="bi bi-file-earmark-excel-fill text-success"></i> Export CSV Report
            </button>
          </div>

          {/* Premium Light KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
            {kpiCards.map((card, i) => (
              <div key={i} style={{
                background: '#fff',
                border: `1px solid #e2e8f0`,
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '54px', height: '54px', borderRadius: '14px',
                  background: card.bg, color: card.accent, border: `1px solid ${card.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px'
                }}>
                  <i className={card.icon}></i>
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '2px' }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, marginTop: '4px' }}>
                    {card.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Content Card */}
          <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
          }}>
            
            {/* Toolbar / Filters */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>Filter Month</label>
                  <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                    color: '#1e293b', fontSize: '14px', fontWeight: 500, padding: '8px 12px', height: '38px',
                    cursor: 'pointer', outline: 'none', minWidth: '130px'
                  }}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>Filter Year</label>
                  <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                    color: '#1e293b', fontSize: '14px', fontWeight: 500, padding: '8px 12px', height: '38px',
                    cursor: 'pointer', outline: 'none', minWidth: '100px'
                  }}>
                    {[...Array(5)].map((_, i) => {
                      const y = new Date().getFullYear() - i;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                </div>

                <div style={{ minWidth: '260px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>Search Worker</label>
                  <Select
                    options={workerOptions}
                    value={selectedWorker}
                    onChange={setSelectedWorker}
                    isClearable
                    placeholder="Search by Name or ID..."
                    styles={selectStyles}
                  />
                </div>

              </div>

              {/* Status Legend directly in Toolbar */}
              <div style={{ 
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', 
                padding: '8px 16px', display: 'flex', gap: '16px', height: '38px', alignItems: 'center'
              }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginRight: '4px' }}>Legend:</span>
                {[
                  { label: 'Present', bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
                  { label: 'Absent', bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' }
                ].map(l => (
                  <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: l.bg, border: `1px solid ${l.dot}` }} />
                    <span style={{ fontSize: '12px', color: '#334155', fontWeight: 600 }}>{l.label}</span>
                  </div>
                ))}
              </div>

            </div>

            {/* Seamless Light Mode Grid */}
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <DataTable
                columns={columns}
                data={gridData}
                pagination
                highlightOnHover
                progressPending={loading}
                fixedHeader
                fixedHeaderScrollHeight="55vh"
                customStyles={tableCustomStyles}
                progressComponent={
                  <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                    <div className="spinner-border text-primary mb-3" role="status"></div>
                    <h5 style={{ fontWeight: 600, color: '#1e293b' }}>Loading Timesheet</h5>
                    <p style={{ fontSize: '14px' }}>Fetching attendance records...</p>
                  </div>
                }
                noDataComponent={
                  <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                    <i className="bi bi-folder-x text-muted" style={{ fontSize: '48px' }}></i>
                    <h5 style={{ fontWeight: 600, color: '#1e293b', marginTop: '16px' }}>No Data Found</h5>
                    <p style={{ fontSize: '14px' }}>Try selecting a different month or year.</p>
                  </div>
                }
              />
            </div>
          </div>

        </main>
      </div>
    </div>
  );
}

export default AttendanceReport;
